import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { EipiAssistantPanel } from '../components/chat/EipiAssistantPanel';
import SpendPill from '../components/admin/SpendPill';
import axios from 'axios';
import {
  Plus, Film, Loader2, Settings, LogOut, Search, Grid3X3, List,
  MoreHorizontal, Users, Clock, Calendar, Folder, ChevronRight,
  Home, FolderOpen, Video, Image, Music, MessageSquare, Star,
  Share2, Trash2, Copy, UserPlus, X, Shield, Menu, Pencil,
  FileText, Layers, Music2, Wand2, Download, Bot, Send, Paperclip, Sparkles, Play,
  Beaker, Heart, Zap, TrendingUp, RotateCw, ArrowRight, Bell, Check, AlertTriangle,
  MonitorUp, Upload
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from '../utils/toast';
const API = window.__API_BASE;

// Cross-env transfer tools are workspace/dev features — only render them on
// the preview environment, never on the live production site.
const IS_PREVIEW_ENV = /preview\.emergentagent\.com|localhost/.test(window.location.hostname);

// Same sidebar nav as ProjectView for consistency
const SIDEBAR_NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, filter: 'all' },
  { id: 'projects', label: 'Projects', icon: FolderOpen, filter: 'all' },
  { id: 'script', label: 'Script', icon: FileText, filter: 'stage-script' },
  { id: 'preproduction', label: 'Pre-Production', icon: Layers, filter: 'stage-preproduction' },
  { id: 'production', label: 'Production', icon: Video, filter: 'stage-production' },
  { id: 'assets', label: 'Assets', icon: Star, filter: 'favorites' },
  { id: 'audio', label: 'Audio', icon: Music2, filter: 'all' },
  { id: 'postprod', label: 'Post-Production', icon: Wand2, filter: 'stage-postproduction' },
  { id: 'exports', label: 'Exports', icon: Download, filter: 'all' },
];

// Maps a project's numeric current_stage (1–17) to a pipeline group so the
// left-nav stage buttons show only the projects currently in that phase.
// Script=1 · Pre-Production=2–6 · Production=7–15 (viz+motion) · Post=16–17.
const STAGE_GROUP_FILTERS = {
  'stage-script': (s) => s <= 1,
  'stage-preproduction': (s) => s >= 2 && s <= 6,
  'stage-production': (s) => s >= 7 && s <= 15,
  'stage-postproduction': (s) => s >= 16,
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // {id, name}
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('Dashboard');
  const [sidebarFilter, setSidebarFilter] = useState('all'); // all, videos, keyframes, favorites
  const [premiumRequests, setPremiumRequests] = useState([]);
  const [showPremiumPanel, setShowPremiumPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Create project form state
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    video_duration: 60,
    max_shots: 15,
    aspect_ratio: '16:9',
    production_mode: 'human',
    entry_mode: 'idea'
  });
  const [creating, setCreating] = useState(false);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  // AI Chat state
  // AI Chat panel - always collapsed on mobile, open on desktop only
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: `Hello! I'm your AI production assistant. How can I help you today?` }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = React.useRef(null);

  // My Studio state
  const [studioData, setStudioData] = useState(null);
  const [studioLoading, setStudioLoading] = useState(true);
  const [topRecipes, setTopRecipes] = useState([]);
  const [recipeCount, setRecipeCount] = useState(null);

  // Incoming nav intent from GlobalSidebar (e.g. Recipes page left panel).
  useEffect(() => {
    const s = location.state;
    if (!s) return;
    if (s.activeNav) setActiveNav(s.activeNav);
    if (s.sidebarFilter) setSidebarFilter(s.sidebarFilter);
    if (s.openCreate) setShowCreateModal(true);
    navigate('/', { replace: true, state: null });
  }, [location.state]);

  useEffect(() => {
    fetchProjects();
    fetchStudioData();
    fetchTopRecipes();
    fetchNotifications();
    // Sidebar badge must always tally the true recipe total.
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/recipes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (Array.isArray(data?.recipes)) setRecipeCount(data.recipes.length); })
      .catch(() => {});
  }, []);

  const fetchStudioData = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/recipes/user/studio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudioData(data);
    } catch (err) {
      console.error('Failed to load studio data:', err);
    } finally {
      setStudioLoading(false);
    }
  };

  const fetchTopRecipes = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/recipes/user/top-recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopRecipes(data.recipes || []);
    } catch (err) {
      console.error('Failed to load top recipes:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      // Explicit auth header (matching fetchStudioData / createProject) —
      // relying on axios.defaults has bitten us in production builds where
      // the default header gets lost on route transitions, producing a
      // silent 401 that surfaces as an empty projects list.
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/projects`, { headers });
      const sorted = res.data.sort((a, b) => 
        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      );
      setProjects(sorted);
    } catch (e) {
      console.error('Failed to fetch projects:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notifRes, reqRes] = await Promise.all([
        axios.get(`${API}/api/premium/notifications`),
        user?.is_admin ? axios.get(`${API}/api/premium/requests`) : Promise.resolve({ data: { requests: [] } }),
      ]);
      setNotifications(notifRes.data.notifications || []);
      setPremiumRequests(reqRes.data.requests || []);
    } catch { /* silent */ }
  };

  const handlePremiumApprove = async (email) => {
    try {
      await axios.post(`${API}/api/premium/approve/${email}`);
      setPremiumRequests(prev => prev.filter(r => r.email !== email));
    } catch (err) {
      console.error('Failed to approve:', err);
    }
  };

  const handlePremiumReject = async (email) => {
    try {
      await axios.post(`${API}/api/premium/reject/${email}`);
      setPremiumRequests(prev => prev.filter(r => r.email !== email));
    } catch (err) {
      console.error('Failed to reject:', err);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Defensive header — don't rely solely on axios.defaults (production
      // builds have historically lost the default header on certain route
      // transitions, causing silent 401s on POST /api/projects).
      const stored = localStorage.getItem('token');
      const headers = stored ? { Authorization: `Bearer ${stored}` } : {};
      const res = await axios.post(`${API}/api/projects`, {
        name: newProject.name,
        description: newProject.description,
        orientation: newProject.aspect_ratio,
        duration: newProject.video_duration,
        max_shots: newProject.max_shots,
        production_mode: newProject.production_mode,
        entry_mode: newProject.entry_mode,
      }, { headers });
      // "idea" entry routes into the Stage 0 multi-model workbench;
      // "script_ready" jumps straight to the existing Stage 1 ingest.
      if (newProject.entry_mode === 'idea') {
        navigate(`/project/${res.data.id}/stage-0`);
      } else {
        navigate(`/project/${res.data.id}`);
      }
    } catch (e) {
      console.error('Failed to create project:', e);
      // Surface to the user — silent 401 was leaving users staring at a
      // do-nothing modal with zero feedback (Bug 1.b, 2026-05-15 brief).
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg = detail || e.message || 'Could not create project';
      if (status === 401) {
        msg = 'Session expired or auth header missing — please refresh and sign in again.';
      } else if (status === 403) {
        msg = 'Your account is not approved to create projects yet. Contact an admin.';
      } else if (status >= 500) {
        msg = `Server error (${status}). Please try again in a moment.`;
      }
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const inviteCollaborator = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await axios.post(`${API}/api/projects/${selectedProject.id}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      setShowInviteModal(false);
      setInviteEmail('');
      fetchProjects();
    } catch (e) {
      console.error('Failed to invite:', e);
    } finally {
      setInviting(false);
    }
  };

  const deleteProject = async (projectId, projectName) => {
    // confirm removed - non-blocking
    try {
      await axios.delete(`${API}/api/projects/${projectId}`);
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete project');
    }
  };

  const renameProject = async () => {
    const name = renameValue.trim();
    if (!name || !renameTarget) return;
    setRenaming(true);
    try {
      await axios.patch(`${API}/api/projects/${renameTarget.id}`, { name });
      setRenameTarget(null);
      toast.success('Project renamed');
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename project');
    } finally {
      setRenaming(false);
    }
  };

  // 2026-05-25 — Highlight the freshly-duplicated project for a moment
  // so the operator sees where the copy landed in the list.
  const [highlightProjectId, setHighlightProjectId] = useState(null);

  const duplicateProject = async (projectId, projectName) => {
    try {
      // 2026-06-20 — backend default is now mode=as_is (true clone with all
      // rendered media + pipeline progress preserved). Pass ?mode=clean for
      // the legacy "strip media, force re-approval" behavior.
      const res = await axios.post(`${API}/api/projects/${projectId}/duplicate?mode=as_is`);
      if (res.data?.success) {
        await fetchProjects();
        const newId = res.data.project_id;
        if (newId) {
          setHighlightProjectId(newId);
          setTimeout(() => setHighlightProjectId(null), 3500);
        }
        toast.success(`Duplicated as-is → "${res.data.name}"`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate project');
    }
  };

  const getProjectProgress = (project) => {
    const stage = project.current_stage || 1;
    return Math.round((stage / 17) * 100);
  };

  // 2026-07-03 — Cross-environment transfer: one-click push to the preview
  // environment; falls back to a portable .json bundle download when the
  // direct push isn't configured (PREVIEW_IMPORT_URL) or fails.
  const [transferringId, setTransferringId] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = React.useRef(null);

  // Pull from Production (2026-07-06) — the platform blocks prod→preview push
  // config, so preview pulls instead.
  const [pullModalOpen, setPullModalOpen] = useState(false);
  const [prodProjects, setProdProjects] = useState([]);
  const [pullLoading, setPullLoading] = useState(false);
  const [pullFilter, setPullFilter] = useState('');

  const openPullModal = async () => {
    setPullModalOpen(true);
    setPullLoading(true);
    try {
      const res = await axios.get(`${API}/api/project-transfer/prod-projects`);
      setProdProjects(res.data?.projects || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not fetch production projects');
      setPullModalOpen(false);
    } finally {
      setPullLoading(false);
    }
  };

  const pullProject = async (proj) => {
    try {
      const res = await axios.post(`${API}/api/project-transfer/pull`, {
        source_project_id: proj.id, name: proj.name,
      });
      if (res.data?.already_importing) {
        toast.info(`"${proj.name}" is already importing — it'll appear shortly.`);
      } else {
        toast.success(`"${proj.name}" is importing — placeholder added to your dashboard.`);
      }
      setPullModalOpen(false);
      await fetchProjects();
      if (res.data?.project_id) {
        setHighlightProjectId(res.data.project_id);
        setTimeout(() => setHighlightProjectId(null), 3500);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pull failed');
    }
  };

  const downloadBundle = async (projectId, projectName) => {
    const res = await axios.get(`${API}/api/projects/${projectId}/export-bundle`);
    const blob = new Blob([JSON.stringify(res.data)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(projectName || 'project').replace(/[^a-z0-9-_ ]/gi, '')}.eipibundle.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyToPreview = async (projectId, projectName) => {
    setTransferringId(projectId);
    try {
      if (!IS_PREVIEW_ENV) {
        // Live site: no cross-env push — the icon acts as a bundle export/backup.
        await downloadBundle(projectId, projectName);
        toast.success('Project bundle exported (.eipibundle.json) — import it on the preview site.');
        return;
      }
      const res = await axios.post(`${API}/api/projects/${projectId}/copy-to-preview`);
      if (res.data?.already_importing) {
        toast.info(`"${projectName}" is already importing on preview — it'll appear there shortly.`);
      } else if (res.data?.success) {
        toast.success(`"${res.data.name}" created on preview — data is transferring in the background.`);
      }
    } catch (e) {
      try {
        await downloadBundle(projectId, projectName);
        toast.info('Direct push unavailable — downloaded bundle file instead. Use "Import Project" on the target environment.');
      } catch (e2) {
        toast.error(e2.response?.data?.detail || e.response?.data?.detail || 'Transfer failed');
      }
    } finally {
      setTransferringId(null);
    }
  };

  const importBundleFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const res = await axios.post(`${API}/api/projects/import-bundle`, bundle);
      if (res.data?.success) {
        await fetchProjects();
        setHighlightProjectId(res.data.project_id);
        setTimeout(() => setHighlightProjectId(null), 3500);
        toast.success(`Imported "${res.data.name}" (${res.data.shots} shots) — media localizing in background`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed — is this a valid .eipibundle.json file?');
    } finally {
      setImporting(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    try {
      const response = await axios.post(`${API}/api/chat`, {
        session_id: `chat_dashboard`,
        message: chatInput,
      });
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response || response.data.message || 'I can help with your filmmaking projects.',
        suggestions: response.data.suggestions,
      }]);
    } catch {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // 2026-05-25 — Project sort. Session-only (resets on refresh).
  const [sortBy, setSortBy] = useState('latest');

  // 2026-06-16 — robust timestamp parser for the "Latest activity" sort.
  // Mongo historically wrote a mix of formats (naive ISO, ISO+offset,
  // "YYYY-MM-DD HH:MM:SS"). The backend now normalizes outgoing
  // timestamps to UTC ISO, but old API responses + cached payloads can
  // still leak through, so we defensively coerce here too.
  const parseTs = (v) => {
    if (!v) return 0;
    let s = String(v).trim();
    if (!s) return 0;
    if (s.endsWith('Z')) s = s.slice(0, -1) + '+00:00';
    // "2026-05-19 02:58:37" → "2026-05-19T02:58:37"
    if (!s.includes('T') && s.includes(' ')) s = s.replace(' ', 'T');
    // If still tz-naive, assume UTC (matches the backend's normalization
    // pass which fills tzinfo from utc for any naive datetime in Mongo).
    if (!/[+-]\d{2}:?\d{2}$/.test(s) && !s.endsWith('+00:00')) s += '+00:00';
    const t = new Date(s).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const filteredProjects = (() => {
    const base = projects.filter(p => {
      // Search project NAME only (Feb 2026 fix). Previously also matched
      // `p.description`, which caused false positives: typing "hy" surfaced
      // IELTS / Amazon / Wrogn / Tide because their descriptions contained
      // words like "hyper-realistic", "physical", "rhythm", etc.
      const matchesSearch = !searchQuery ||
        p.name?.toLowerCase().includes(searchQuery.toLowerCase());
      if (sidebarFilter === 'videos') return matchesSearch && p.current_stage >= 13;
      if (sidebarFilter === 'keyframes') return matchesSearch && p.current_stage >= 7;
      if (sidebarFilter === 'favorites') return matchesSearch && p.favorited;
      if (STAGE_GROUP_FILTERS[sidebarFilter]) {
        return matchesSearch && STAGE_GROUP_FILTERS[sidebarFilter](p.current_stage || 1);
      }
      return matchesSearch;
    });
    const cmp = {
      // "Latest activity" = max(last shot edit, project.updated_at, created_at).
      // The backend now computes this as `last_activity_at`; we fall back to
      // updated_at / created_at for older API responses.
      latest: (a, b) => parseTs(b.last_activity_at || b.updated_at || b.created_at) - parseTs(a.last_activity_at || a.updated_at || a.created_at),
      newest: (a, b) => parseTs(b.created_at) - parseTs(a.created_at),
      stage: (a, b) => (b.current_stage || 0) - (a.current_stage || 0),
      shots: (a, b) => (b.shot_count || b.shots_count || 0) - (a.shot_count || a.shots_count || 0),
      alpha: (a, b) => (a.name || '').localeCompare(b.name || ''),
    }[sortBy] || ((a, b) => 0);
    return [...base].sort(cmp);
  })();

  // Smart dashboard: curated slices
  const activeProjects = projects.filter(p => getProjectProgress(p) < 100).slice(0, 4);
  const completedCount = projects.filter(p => getProjectProgress(p) === 100).length;
  const inProgressCount = projects.filter(p => getProjectProgress(p) < 100).length;
  const showAllProjects = activeNav !== 'Dashboard' || searchQuery;

  if (loading) {
    return (
      <div className="min-h-screen bg-ai-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-sm bg-ai-bg-secondary border border-ai-border flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-ai-accent" />
          </div>
          <p className="text-ai-text-secondary font-ai text-sm">LOADING_PROJECTS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen max-w-[100vw] overflow-hidden bg-ai-bg flex relative">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — HUMAN/WARM Style (matches ProjectView) */}
      <aside className={`
        fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50
        w-56 flex flex-col overflow-hidden
        transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ background: 'linear-gradient(180deg, #0B1120 0%, #101B2E 50%, #0D1526 100%)', borderRight: '1px solid rgba(56, 189, 248, 0.08)' }}>
        <BrandLogo />

        {/* New Project Button — matches second row h-12 (48px) */}
        <div className="h-12 px-3 flex items-center border-b" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <button onClick={() => setShowCreateModal(true)} className="w-full px-4 py-2 rounded-lg font-ai font-semibold text-xs uppercase tracking-wider transition-all hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #162D4A 100%)', color: '#94C4F7', boxShadow: '0 4px 15px rgba(30, 58, 95, 0.4)', border: '1px solid rgba(56, 189, 248, 0.12)' }}
            data-testid="new-project-btn">
            <Plus className="w-4 h-4 inline mr-2" />New Project
          </button>
        </div>

        {/* Import / Pull — preview environment only */}
        {IS_PREVIEW_ENV && (
        <div className="px-3 py-1.5 border-b" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <button type="button" className="w-full flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg font-ai text-[10px] uppercase tracking-wider cursor-pointer transition-all hover:brightness-125"
            style={{ color: '#5B7A9E', border: '1px dashed rgba(56, 189, 248, 0.15)', background: 'transparent' }}
            title="Import a project bundle (.eipibundle.json) exported from another environment"
            disabled={importing}
            onClick={() => importInputRef.current?.click()}
            data-testid="import-project-btn">
            {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {importing ? 'Importing…' : 'Import Project'}
          </button>
          <input ref={importInputRef} type="file" accept=".json,application/json,text/json" className="hidden"
            onChange={(e) => { importBundleFile(e.target.files?.[0]); e.target.value = ''; }} />
          <button type="button" className="w-full mt-1.5 flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg font-ai text-[10px] uppercase tracking-wider cursor-pointer transition-all hover:brightness-125"
            style={{ color: '#5B7A9E', border: '1px dashed rgba(56, 189, 248, 0.15)', background: 'transparent' }}
            title="Pull a project from the production environment into this one"
            onClick={openPullModal}
            data-testid="pull-from-prod-btn">
            <MonitorUp className="w-3 h-3" />
            Pull from Production
          </button>
        </div>
        )}

        {/* Pull from Production picker */}
        {pullModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPullModalOpen(false)} data-testid="pull-modal">
            <div className="w-full max-w-md rounded-2xl p-4 max-h-[70vh] flex flex-col" style={{ background: '#0B1420', border: '1px solid rgba(56,189,248,0.15)' }} onClick={(e) => e.stopPropagation()}>
              <h3 className="font-ai text-sm text-white/80 uppercase tracking-wider mb-3">Pull from Production</h3>
              <input
                type="text" placeholder="Filter projects…" value={pullFilter}
                onChange={(e) => setPullFilter(e.target.value)}
                className="w-full px-3 py-2 mb-3 rounded-lg text-xs text-white/80 placeholder-white/25 bg-white/[0.04] border border-white/[0.08] focus:outline-none"
                data-testid="pull-filter-input"
              />
              <div className="flex-1 overflow-y-auto space-y-1">
                {pullLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-ai-accent" /></div>
                ) : (
                  prodProjects
                    .filter(p => (p.name || '').toLowerCase().includes(pullFilter.toLowerCase()))
                    .map(p => (
                      <button key={p.id} onClick={() => pullProject(p)}
                        title={p.name}
                        className="w-full text-left px-3 py-2 rounded-lg text-xs text-white/70 hover:bg-white/[0.06] transition-colors flex items-center justify-between"
                        data-testid={`pull-project-${p.id}`}>
                        <span className="truncate">{p.name}</span>
                        <span className="text-[9px] text-white/30 flex-shrink-0 ml-2">{(p.updated_at || p.created_at || '').slice(0, 10)}</span>
                      </button>
                    ))
                )}
                {!pullLoading && prodProjects.length === 0 && (
                  <p className="text-xs text-white/30 text-center py-6">No projects found on production.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation — matching ProjectView exactly */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-hidden">
          {SIDEBAR_NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveNav(item.label);
                setSidebarFilter(item.filter);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-ai font-semibold uppercase tracking-wider transition-all duration-200 ${
                activeNav === item.label ? 'text-human-text' : 'hover:bg-[#162D4A]/50'
              }`}
              style={activeNav === item.label ? {
                background: 'rgba(56, 189, 248, 0.08)',
                color: '#E2E8F0',
                boxShadow: '0 0 12px rgba(56, 189, 248, 0.06)'
              } : { color: '#64748B' }}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{item.label}</span>
            </button>
          ))}

          {/* Creative Tools — inside scrollable nav */}
          <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(245, 158, 11, 0.12)' }}>
            <p className="px-3 mb-2 text-[9px] font-ai font-bold uppercase tracking-[0.2em]" style={{ color: 'rgba(245, 158, 11, 0.5)' }}>Creative Tools</p>
            <button onClick={() => { navigate('/recipes'); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-ai uppercase tracking-wider rounded-lg transition-all hover:bg-[#3D2A0A]/40 group"
              style={{ color: '#F59E0B' }}
              data-testid="sidebar-recipes">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <Beaker className="w-3.5 h-3.5 transition-transform group-hover:scale-110" style={{ color: '#F59E0B' }} />
              </div>
              <span>Recipes</span>
              <span className="ml-auto text-[9px] font-bold" style={{ color: 'rgba(245, 158, 11, 0.4)' }} data-testid="sidebar-recipe-count">{recipeCount ?? ''}</span>
            </button>
            <button onClick={() => { navigate('/llm-studio'); setSidebarOpen(false); }} className="w-full mt-1 flex items-center gap-3 px-3 py-2.5 text-xs font-ai uppercase tracking-wider rounded-lg transition-all hover:bg-[#3D2A0A]/40 group"
              style={{ color: '#F59E0B' }}
              data-testid="sidebar-llms">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.12)' }}>
                <Bot className="w-3.5 h-3.5 transition-transform group-hover:scale-110" style={{ color: '#F59E0B' }} />
              </div>
              <span>LLMs</span>
              <span className="ml-auto text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.20)', color: '#c7d2fe' }}>NEW</span>
            </button>
          </div>
        </nav>

        {/* User / Logout / Admin — identical to ProjectView */}
        <div className="p-3 border-t relative z-[70]" style={{ borderColor: 'rgba(56, 189, 248, 0.08)' }}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#162D4A]/50 transition-colors">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(56, 189, 248, 0.1)' }}>
              <span className="text-xs font-ai" style={{ color: '#7DD3FC' }}>{user?.name?.[0] || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-ai truncate" style={{ color: '#CBD5E1' }}>{user?.name || 'User'}</p>
            </div>
            <button onClick={logout} className="p-1.5 hover:bg-[#162D4A]/50 rounded transition-colors" title="Sign out">
              <LogOut className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
            </button>
          </div>
          {user?.is_admin && (
            <button onClick={() => navigate('/admin')} className="w-full flex items-center gap-3 px-3 py-2 mt-1 text-xs font-ai uppercase tracking-wider rounded-lg transition-colors hover:bg-[#162D4A]/50" style={{ color: '#7DD3FC' }}>
              <Shield className="w-3.5 h-3.5" />
              <span>Admin Panel</span>
            </button>
          )}
          {user?.is_admin && (
            <button onClick={() => navigate('/admin/debug')} className="w-full flex items-center gap-3 px-3 py-2 mt-1 text-xs font-ai uppercase tracking-wider rounded-lg transition-colors hover:bg-[#162D4A]/50" style={{ color: '#F59E0B' }}>
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Debug Panel</span>
            </button>
          )}
          {/* Premium requests notification (admin only) */}
          {user?.is_admin && premiumRequests.length > 0 && (
            <div className="mt-1">
              <button onClick={() => setShowPremiumPanel(!showPremiumPanel)}
                className="w-full flex items-center gap-3 px-3 py-2 text-xs font-ai uppercase tracking-wider rounded-lg transition-colors hover:bg-[#162D4A]/50"
                style={{ color: '#FF9500' }}
                data-testid="premium-requests-toggle">
                <div className="relative">
                  <Bell className="w-3.5 h-3.5" />
                  <span className="absolute -top-1 -right-1.5 w-3.5 h-3.5 rounded-full bg-[#FF9500] text-[7px] font-bold text-white flex items-center justify-center">
                    {premiumRequests.length}
                  </span>
                </div>
                <span>Premium Requests</span>
              </button>
              {showPremiumPanel && (
                <div className="mt-1 mx-2 p-2 rounded-lg border border-[#FF9500]/20" style={{ background: 'rgba(255,149,0,0.06)' }}>
                  {premiumRequests.map(req => (
                    <div key={req.email} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/5" data-testid={`premium-req-${req.email}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-medium text-white truncate">{req.name || req.email}</p>
                        <p className="text-[8px] text-white/40 truncate">{req.email}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <button onClick={() => handlePremiumApprove(req.email)}
                          className="w-5 h-5 rounded flex items-center justify-center bg-green-500/20 hover:bg-green-500/40 transition-colors"
                          title="Approve" data-testid={`approve-${req.email}`}>
                          <Check className="w-3 h-3 text-green-400" />
                        </button>
                        <button onClick={() => handlePremiumReject(req.email)}
                          className="w-5 h-5 rounded flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 transition-colors"
                          title="Reject" data-testid={`reject-${req.email}`}>
                          <X className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main content - AI/COOL Style */}
      <main className="flex-1 min-w-0 flex flex-col h-screen relative z-10 bg-ai-bg" style={{ backgroundImage: 'linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        {/* Duality of Creation background — centered to main panel */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          backgroundImage: 'url(/assets/duality-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.06
        }} />
        {/* Header */}
        <header className="h-14 border-b border-ai-border flex items-center justify-between px-4 lg:px-6 bg-ai-bg/80 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-ai-bg-secondary rounded-sm lg:hidden"
            >
              <Menu className="w-5 h-5 text-ai-text-secondary" />
            </button>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ai-text-secondary" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-80 pl-10 pr-4 py-2 bg-ai-bg-secondary border border-ai-border rounded-sm
                         text-sm font-ai text-ai-text placeholder-ai-text-secondary
                         focus:outline-none focus:border-ai-accent transition-colors"
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <SpendPill isAdmin={user?.is_admin} />
            {/* View toggle */}
            <div className="flex items-center bg-ai-bg-secondary rounded-sm p-1 border border-ai-border">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-sm transition-colors ${
                  viewMode === 'grid' ? 'bg-ai-accent text-white' : 'text-ai-text-secondary hover:text-ai-text'
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-sm transition-colors ${
                  viewMode === 'list' ? 'bg-ai-accent text-white' : 'text-ai-text-secondary hover:text-ai-text'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-ai-accent text-white font-ai text-xs uppercase tracking-wider rounded-sm border border-ai-accent hover:bg-ai-accent/80 transition-all shadow-ai-glow"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4" />
              <span>New Project</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-3 sm:p-6 overflow-auto overflow-x-hidden">

          {/* Smart Dashboard View */}
          {!showAllProjects ? (
            <>
              {/* Welcome + Stats Row */}
              <div className="mb-6">
                <h1 className="font-ai text-xl font-medium text-ai-text mb-1 tracking-tight">
                  Welcome back, {user?.name?.split(' ')[0] || 'Creator'}
                </h1>
                <p className="text-ai-text-secondary font-ai text-xs">{projects.length} productions &middot; {inProgressCount} active &middot; {completedCount} completed</p>
              </div>

              {/* Stats Cards */}
              {studioData && studioData.stats.total_runs > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="dashboard-stats">
                  <div className="rounded-lg p-3" style={{ background: 'rgba(14, 165, 233, 0.06)', border: '1px solid rgba(14, 165, 233, 0.12)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Play className="w-3.5 h-3.5 text-[#0EA5E9]" />
                      <span className="text-[10px] text-white/40 font-ai uppercase tracking-wider">Total Runs</span>
                    </div>
                    <span className="text-xl font-semibold text-white" data-testid="stat-total-runs">{studioData.stats.total_runs}</span>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'rgba(34, 197, 94, 0.06)', border: '1px solid rgba(34, 197, 94, 0.12)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-3.5 h-3.5 text-[#22C55E]" />
                      <span className="text-[10px] text-white/40 font-ai uppercase tracking-wider">Completed</span>
                    </div>
                    <span className="text-xl font-semibold text-white" data-testid="stat-completed">{studioData.stats.completed_runs}</span>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.12)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-[10px] text-white/40 font-ai uppercase tracking-wider">Credits Used</span>
                    </div>
                    <span className="text-xl font-semibold text-white" data-testid="stat-credits">{studioData.stats.total_credits.toLocaleString()}</span>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'rgba(168, 85, 247, 0.06)', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Heart className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-[10px] text-white/40 font-ai uppercase tracking-wider">Most Used</span>
                    </div>
                    {studioData.stats.most_used ? (
                      <button onClick={() => navigate(`/recipes/${studioData.stats.most_used.slug}`)} className="text-sm font-semibold text-white truncate hover:text-[#0EA5E9] transition-colors block max-w-full text-left" data-testid="stat-most-used">
                        {studioData.stats.most_used.name}
                      </button>
                    ) : (
                      <span className="text-sm text-white/30">&mdash;</span>
                    )}
                  </div>
                </div>
              )}

              {/* Active Projects — max 4 */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-ai text-sm font-medium text-ai-text uppercase tracking-wider flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-ai-accent" />
                    Active Projects
                  </h2>
                  <button onClick={() => { setActiveNav('Projects'); setSidebarFilter('all'); }} className="flex items-center gap-1 text-xs text-ai-accent hover:text-ai-accent/80 transition-colors font-ai" data-testid="view-all-projects">
                    View All ({projects.length}) <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                {activeProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}>
                    <Film className="w-8 h-8 text-ai-text-secondary mb-3" />
                    <p className="text-ai-text-secondary font-ai text-sm mb-3">No active projects</p>
                    <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-ai-accent text-white font-ai text-xs uppercase tracking-wider rounded-sm hover:bg-ai-accent/80 transition-all">
                      <Plus className="w-4 h-4" /> New Project
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {activeProjects.map((project) => (
                      <ProjectCard isAdmin={user?.is_admin}
                        key={project.id}
                        project={project}
                        progress={getProjectProgress(project)}
                        viewMode="grid"
                        highlight={highlightProjectId === project.id}
                        onClick={() => navigate(`/project/${project.id}`)}
                        onInvite={() => { setSelectedProject(project); setShowInviteModal(true); }}
                        onDelete={() => deleteProject(project.id, project.name)}
                        onRename={() => { setRenameTarget({ id: project.id, name: project.name }); setRenameValue(project.name || ''); }}
                        onDuplicate={() => duplicateProject(project.id, project.name)}
                        onCopyToPreview={() => copyToPreview(project.id, project.name)}
                        transferring={transferringId === project.id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Top Recipes */}
              {topRecipes.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-ai text-sm font-medium text-ai-text uppercase tracking-wider flex items-center gap-2">
                      <Beaker className="w-4 h-4 text-amber-400" />
                      Most Used Recipes
                    </h2>
                    <button onClick={() => navigate('/recipes')} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors font-ai" data-testid="browse-recipes-link">
                      Browse All <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    {topRecipes.map(recipe => (
                      <button
                        key={recipe.slug}
                        onClick={() => navigate(`/recipes/${recipe.slug}`)}
                        className="rounded-lg overflow-hidden text-left transition-all hover:scale-[1.02] group"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                        data-testid={`dash-recipe-${recipe.slug}`}
                      >
                        <div className="relative h-24 overflow-hidden bg-black/40">
                          {recipe.thumbnail_url ? (
                            <StudioAuthImage
                              src={recipe.thumbnail_url.startsWith('http') ? recipe.thumbnail_url : `${API}${recipe.thumbnail_url}`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              fallbackLabel={recipe.name}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(11,15,25,0.6) 100%)' }}>
                              <Beaker className="w-6 h-6 text-white/10" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-xs font-medium text-white truncate">{recipe.name}</p>
                          </div>
                          {(recipe.run_count || 0) > 0 && (
                            <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[8px] font-bold bg-sky-500/20 text-sky-400">{recipe.run_count} runs</div>
                          )}
                        </div>
                        <div className="p-2 flex items-center justify-between">
                          <span className="text-[9px] text-white/40">{recipe.category}</span>
                          <span className="text-[9px] text-ai-accent">{recipe.pricing?.credits} credits</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {studioData && studioData.recent_runs?.length > 0 && (
                <div className="mb-6" data-testid="dashboard-recent-activity">
                  <h2 className="font-ai text-sm font-medium text-ai-text uppercase tracking-wider flex items-center gap-2 mb-4">
                    <Clock className="w-4 h-4 text-white/40" />
                    Recent Activity
                  </h2>
                  <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                    {studioData.recent_runs.slice(0, 5).map(run => (
                      <button
                        key={run.run_id}
                        onClick={() => navigate(`/recipes/${run.recipe_slug}`)}
                        className="flex-shrink-0 w-44 rounded-lg overflow-hidden text-left transition-all hover:scale-[1.02] group"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                        data-testid={`dash-run-${run.run_id}`}
                      >
                        <div className="relative h-20 overflow-hidden bg-black/40">
                          {run.thumbnail ? (
                            <StudioAuthImage src={run.thumbnail.startsWith('http') ? run.thumbnail : `${API}${run.thumbnail}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" fallbackLabel={run.recipe_name} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: run.status === 'failed' ? 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(11,15,25,0.6) 100%)' : 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(11,15,25,0.6) 100%)' }}>
                              <span className="text-lg font-bold text-white/10">{(run.recipe_name || '?')[0]}</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                          <div className="absolute top-2 right-2">
                            <div className={`w-2 h-2 rounded-full ${run.status === 'completed' ? 'bg-[#22C55E]' : run.status === 'processing' ? 'bg-amber-400 animate-pulse' : 'bg-red-400'}`} />
                          </div>
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-[10px] font-medium text-white truncate">{run.recipe_name}</p>
                          </div>
                        </div>
                        <div className="p-2 flex items-center justify-between">
                          <span className="text-[9px] text-white/30">{run.output_count} output{run.output_count !== 1 ? 's' : ''}</span>
                          <span className="text-[9px] text-white/20">{run.started_at ? new Date(run.started_at).toLocaleDateString() : ''}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Full Projects List (when searching or non-Dashboard nav) */
            <>
              <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="font-ai text-2xl font-medium text-ai-text mb-1 tracking-tight">PROJECTS</h1>
                  <p className="text-ai-text-secondary font-ai text-sm">// {filteredProjects.length} ACTIVE_PRODUCTIONS</p>
                </div>
                {/* 2026-05-25 — Project sort (session-only). */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-ai uppercase tracking-wider text-ai-text-secondary">Sort</label>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="bg-ai-bg-secondary border border-ai-border text-ai-text text-xs font-ai px-3 py-2 rounded-sm focus:outline-none focus:border-ai-accent"
                    data-testid="dashboard-sort-dropdown"
                  >
                    <option value="latest">Latest activity</option>
                    <option value="newest">Newest project</option>
                    <option value="stage">Highest stage reached</option>
                    <option value="shots">Most shots</option>
                    <option value="alpha">Alphabetical</option>
                  </select>
                </div>
              </div>

              {filteredProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-20 h-20 rounded-sm bg-ai-bg-secondary border border-ai-border flex items-center justify-center mb-6">
                    <Film className="w-10 h-10 text-ai-text-secondary" />
                  </div>
                  <h3 className="font-ai text-xl font-medium text-ai-text mb-2">NO_PROJECTS_FOUND</h3>
                  <p className="text-ai-text-secondary font-ai text-sm mb-6">// Initialize your first production</p>
                  <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-6 py-3 bg-ai-accent text-white font-ai text-xs uppercase tracking-wider rounded-sm border border-ai-accent hover:bg-ai-accent/80 transition-all shadow-ai-glow">
                    <Plus className="w-5 h-5" /> Create Project
                  </button>
                </div>
              ) : (
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
                  : 'space-y-3'
                }>
                  {filteredProjects.map((project) => (
                    <ProjectCard isAdmin={user?.is_admin}
                      key={project.id}
                      project={project}
                      progress={getProjectProgress(project)}
                      viewMode={viewMode}
                      highlight={highlightProjectId === project.id}
                      onClick={() => navigate(`/project/${project.id}`)}
                      onInvite={() => { setSelectedProject(project); setShowInviteModal(true); }}
                      onDelete={() => deleteProject(project.id, project.name)}
                      onRename={() => { setRenameTarget({ id: project.id, name: project.name }); setRenameValue(project.name || ''); }}
                      onDuplicate={() => duplicateProject(project.id, project.name)}
                      onCopyToPreview={() => copyToPreview(project.id, project.name)}
                      transferring={transferringId === project.id}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)} title="Create New Project">
          <form onSubmit={createProject} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Project Name</label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="My Film Project"
                className="input-dark"
                required
                data-testid="project-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Description</label>
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief description of your project..."
                className="input-dark resize-none h-20"
                data-testid="project-desc-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">Aspect Ratio</label>
                <div className="flex gap-2">
                  {['16:9', '9:16', '1:1'].map((ratio) => (
                    <button
                      key={ratio}
                      type="button"
                      onClick={() => setNewProject({ ...newProject, aspect_ratio: ratio })}
                      className={`flex-1 px-3 py-2 text-sm border transition-colors rounded-sm font-ai ${
                        newProject.aspect_ratio === ratio
                          ? 'bg-ai-accent/15 border-ai-accent text-ai-accent'
                          : 'bg-ai-bg border-ai-border text-ai-text-secondary hover:border-ai-accent/50'
                      }`}
                    >
                      {ratio}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">Max Shots</label>
                <input
                  type="number"
                  value={newProject.max_shots}
                  onChange={(e) => setNewProject({ ...newProject, max_shots: parseInt(e.target.value) || 15 })}
                  min={5}
                  max={75}
                  className="input-dark"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Video Duration (seconds)</label>
              <input
                type="number"
                value={newProject.video_duration}
                onChange={(e) => setNewProject({ ...newProject, video_duration: parseInt(e.target.value) || 60 })}
                min={15}
                max={300}
                className="input-dark"
              />
            </div>

            {/* Cost estimate */}
            <div className="p-4 bg-ai-bg rounded-sm border border-ai-border">
              <div className="flex items-center justify-between">
                <span className="text-ai-text-secondary font-ai text-sm">Estimated Cost</span>
                <span className="text-2xl font-bold text-ai-accent font-ai">
                  ₹{Math.round(newProject.max_shots * 1.17 * 83)}
                </span>
              </div>
              <p className="text-xs text-ai-text-secondary font-ai mt-1">Based on {newProject.max_shots} shots (~${(newProject.max_shots * 1.17).toFixed(0)} USD)</p>
            </div>

            {/* Production Mode */}
            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Production Mode</label>
              <div className="flex gap-3">
                {[
                  { id: 'human', label: 'Human-in-the-Loop', desc: 'Review & approve at each stage' },
                  { id: 'auto', label: 'Full Auto', desc: 'AI runs all 17 stages automatically' }
                ].map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setNewProject({ ...newProject, production_mode: mode.id })}
                    data-testid={`mode-${mode.id}`}
                    className={`flex-1 p-3 rounded-sm border text-left transition-all font-ai ${
                      newProject.production_mode === mode.id
                        ? 'bg-ai-accent/15 border-ai-accent text-ai-accent'
                        : 'bg-ai-bg border-ai-border text-ai-text-secondary hover:border-ai-accent/50'
                    }`}
                  >
                    <div className="text-sm font-medium">{mode.label}</div>
                    <div className="text-xs text-ai-text-secondary mt-0.5">{mode.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* How are you starting? — toggle that routes the user into
             *  Stage 0 (multi-model script workbench) or skips to Stage 1
             *  (existing Script Ingest). Default "idea" because the
             *  ensemble workbench is the new product moat — three frontier
             *  LLMs in parallel + Opus synthesis. */}
            <div data-testid="entry-mode-section">
              <label className="block text-sm font-medium text-txt-secondary mb-2">How are you starting?</label>
              <div className="flex flex-col gap-2">
                {[
                  {
                    id: 'idea',
                    label: 'Start from an idea',
                    desc: "Open the Idea to Script workbench. Three AIs draft from your brief; EiPiCenter's Director AI picks or synthesizes the strongest."
                  },
                  {
                    id: 'script_ready',
                    label: 'I have a script ready',
                    desc: 'Skip to Stage 1 Script Ingest and paste your own.'
                  }
                ].map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setNewProject({ ...newProject, entry_mode: mode.id })}
                    data-testid={`entry-mode-${mode.id}`}
                    className={`p-3 rounded-sm border text-left transition-all font-ai ${
                      newProject.entry_mode === mode.id
                        ? 'bg-ai-accent/15 border-ai-accent text-ai-accent'
                        : 'bg-ai-bg border-ai-border text-ai-text-secondary hover:border-ai-accent/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full border ${newProject.entry_mode === mode.id ? 'border-ai-accent bg-ai-accent' : 'border-ai-text-secondary/40'}`}>
                        {newProject.entry_mode === mode.id && <div className="w-1.5 h-1.5 rounded-full bg-white m-auto mt-[3px]"></div>}
                      </div>
                      <div className="text-sm font-medium">{mode.label}</div>
                    </div>
                    <div className="text-xs text-ai-text-secondary mt-1 ml-5">{mode.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !newProject.name}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
                data-testid="create-project-submit"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Project'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Invite Modal */}
      {renameTarget && (
        <Modal onClose={() => setRenameTarget(null)} title="Rename Project">
          <form onSubmit={(e) => { e.preventDefault(); renameProject(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Project name</label>
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="input-dark"
                maxLength={120}
                autoFocus
                required
                data-testid="rename-project-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRenameTarget(null)}
                className="px-4 py-2 text-sm font-ai text-ai-text-secondary hover:text-ai-text transition-colors"
                data-testid="rename-project-cancel">
                Cancel
              </button>
              <button type="submit" disabled={renaming || !renameValue.trim() || renameValue.trim() === renameTarget.name}
                className="px-4 py-2 text-sm font-ai bg-ai-accent/15 border border-ai-accent text-ai-accent rounded-sm hover:bg-ai-accent/25 transition-colors disabled:opacity-40"
                data-testid="rename-project-save">
                {renaming ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showInviteModal && selectedProject && (
        <Modal onClose={() => setShowInviteModal(false)} title="Invite Collaborator">
          <form onSubmit={inviteCollaborator} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Email Address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@eipimedia.com"
                className="input-dark"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Role</label>
              <div className="flex gap-2">
                {['viewer', 'editor'].map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setInviteRole(role)}
                    className={`flex-1 px-4 py-2 text-sm rounded-sm border capitalize transition-colors font-ai ${
                      inviteRole === role
                        ? 'bg-ai-accent/15 border-ai-accent text-ai-accent'
                        : 'bg-ai-bg border-ai-border text-ai-text-secondary hover:border-ai-accent/50'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviting || !inviteEmail}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                {inviting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Invite'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* AI Chat Panel — ChatGPT-like global assistant (GPT-5.5 + image gen) */}
      <EipiAssistantPanel
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        userInitial={user?.name?.[0] || 'U'}
      />

      {/* Chat toggle button when closed */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          data-testid="open-chat-btn"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center z-50 hover:scale-110 transition-all duration-300 overflow-hidden"
          style={{ background: '#0EA5E9' }}
        >
          <img src="https://customer-assets.emergentagent.com/job_a2435b19-3cc0-4261-90b4-1cf9d208aea9/artifacts/h74r9xmb_image.png" alt="EIPI Bot" className="w-10 h-10 rounded-full object-cover" />
        </button>
      )}
    </div>
  );
}

// Project Card Component - DUALITY DESIGN (Split warm/cool)
function ProjectCard({ project, progress, viewMode, onClick, onInvite, onDelete, onDuplicate, onRename, onCopyToPreview, transferring, isAdmin, highlight }) {
  const [showMenu, setShowMenu] = useState(false);

  if (viewMode === 'list') {
    return (
      <div 
        onClick={onClick}
        className={`bg-ai-bg-secondary border ${highlight ? 'border-ai-accent shadow-[0_0_0_2px_rgba(14,165,233,0.35)] animate-pulse' : 'border-ai-border'} rounded-sm p-4 flex items-center gap-4 cursor-pointer group hover:border-ai-accent transition-all`}
        data-testid="project-card"
      >
        {/* Thumbnail */}
        <div className="w-24 h-14 rounded-sm overflow-hidden bg-human-bg-secondary flex-shrink-0">
          {project.thumbnail_url ? (
            <img 
              src={project.thumbnail_url.startsWith('http') ? project.thumbnail_url : `${API}${project.thumbnail_url}`}
              alt={project.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div className={`w-full h-full items-center justify-center ${project.thumbnail_url ? 'hidden' : 'flex'}`}
            style={{ background: 'linear-gradient(135deg, #1A1614, #26201B)' }}>
            <span className="text-lg font-human font-bold text-human-accent">{(project.name || 'P')[0].toUpperCase()}</span>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-ai font-medium text-ai-text group-hover:text-ai-accent transition-colors truncate">
            {project.name}
          </h3>
          {isAdmin && project.owner_email && (
            <p className="text-[10px] text-ai-accent/60 truncate">{project.owner_email}</p>
          )}
          <p className="text-sm font-ai text-ai-text-secondary truncate">{project.description || 'No description'}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-ai font-medium text-ai-text">{progress}%</p>
            <p className="text-xs font-ai text-ai-text-secondary">COMPLETE</p>
          </div>
          <div className="w-24 h-1 bg-ai-border rounded-full overflow-hidden">
            <div className="h-full bg-ai-accent rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onRename(); }}
            className="p-2 hover:bg-ai-bg rounded-sm transition-colors"
            title="Rename project"
            data-testid="rename-project-btn"
          >
            <Pencil className="w-4 h-4 text-ai-text-secondary" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onInvite(); }}
            className="p-2 hover:bg-ai-bg rounded-sm transition-colors"
          >
            <UserPlus className="w-4 h-4 text-ai-text-secondary" />
          </button>
          <ChevronRight className="w-5 h-5 text-ai-text-secondary" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-ai-bg-secondary border ${highlight ? 'border-ai-accent shadow-[0_0_0_2px_rgba(14,165,233,0.35)] animate-pulse' : 'border-ai-border'} rounded-sm overflow-hidden group cursor-pointer hover:border-ai-accent hover:shadow-ai-glow transition-all`}
      onClick={onClick}
      data-testid="project-card"
    >
      {/* Split Thumbnail - Left warm, right cool */}
      <div className="flex">
        {/* Left: Poster (Warm tint) */}
        <div className="w-1/2 aspect-video bg-human-bg-secondary relative overflow-hidden">
          {project.thumbnail_url ? (
            <img 
              src={project.thumbnail_url.startsWith('http') ? project.thumbnail_url : `${API}${project.thumbnail_url}`} 
              alt={project.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div className={`w-full h-full flex flex-col items-center justify-center ${project.thumbnail_url ? 'hidden' : 'flex'}`}
            style={{ background: 'linear-gradient(135deg, #1A1614, #26201B)' }}>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2"
              style={{ background: 'rgba(212,163,115,0.15)', border: '1px solid rgba(212,163,115,0.3)' }}>
              <span className="text-xl font-human font-bold text-human-accent">{(project.name || 'P')[0].toUpperCase()}</span>
            </div>
          </div>
        </div>

        {/* Right: Metadata (Cool) */}
        <div className="w-1/2 p-3 bg-ai-bg flex flex-col">
          <p className="font-ai text-ai-accent text-[10px] tracking-wider uppercase">{project.production_mode === 'human' ? 'HUMAN' : project.production_mode === 'telegram' ? 'TELEGRAM' : 'AUTO'}</p>
          <h3 className="font-ai text-ai-text text-sm mt-1 truncate group-hover:text-ai-accent transition-colors">{project.name}</h3>
          
          {/* Progress */}
          <div className="mt-auto pt-3 space-y-1">
            <div className="w-full h-1 bg-ai-border rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${progress === 100 ? 'bg-status-success' : 'bg-ai-accent'}`} style={{ width: `${progress}%` }} />
            </div>
            <p className={`font-ai text-[9px] ${progress === 100 ? 'text-status-success' : 'text-ai-text-secondary'}`}>
              {progress === 100 ? 'COMPLETE' : `${progress}% COMPLETE`}
            </p>
          </div>
          
          <p className="font-ai text-ai-text-secondary text-[8px] mt-2">STAGE: {getStageLabel(project.current_stage || 1)}</p>
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-ai-border bg-ai-bg">
        <div className="flex items-center gap-1 text-[10px] font-ai text-ai-text-secondary">
          <Calendar className="w-3 h-3" />
          <span>{new Date(project.created_at).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onRename(); }}
            className="p-1.5 hover:bg-ai-bg-secondary rounded-sm transition-colors"
            title="Rename project"
            data-testid="rename-project-btn"
          >
            <Pencil className="w-3.5 h-3.5 text-ai-text-secondary hover:text-ai-accent" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="p-1.5 hover:bg-ai-bg-secondary rounded-sm transition-colors"
            title="Duplicate project"
            data-testid="duplicate-project-btn"
          >
            <Copy className="w-3.5 h-3.5 text-ai-text-secondary hover:text-ai-accent" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); if (!transferring) onCopyToPreview(); }}
            className="p-1.5 hover:bg-ai-bg-secondary rounded-sm transition-colors"
            title={IS_PREVIEW_ENV ? "Copy to Preview environment (falls back to bundle download)" : "Export project bundle (.json) for preview import"}
            data-testid="copy-to-preview-btn"
            disabled={transferring}
          >
            {transferring
              ? <Loader2 className="w-3.5 h-3.5 animate-spin text-ai-accent" />
              : <MonitorUp className="w-3.5 h-3.5 text-ai-text-secondary hover:text-ai-accent" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onInvite(); }}
            className="p-1.5 hover:bg-ai-bg-secondary rounded-sm transition-colors"
            title="Invite collaborator"
          >
            <UserPlus className="w-3.5 h-3.5 text-ai-text-secondary hover:text-ai-accent" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 hover:bg-red-500/10 rounded-sm transition-colors"
            title="Delete project"
            data-testid="delete-project-btn"
          >
            <Trash2 className="w-3.5 h-3.5 text-ai-text-secondary hover:text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function for stage labels
function getStageLabel(stage) {
  const stages = ['INIT', 'SCRIPT', 'SHOTS', 'ASSETS', 'SKETCHES', 'ANGLES', 'CONFIRM', 'KEYFRAMES', 'VLM_QA', 'IMPROVE', 'CD_LOCK', 'NEUTRAL', 'LINEUP', 'VIDEO', 'VIDEO_QA', 'LOCK', 'EXPORT', 'TIMELINE'];
  return stages[stage] || 'UNKNOWN';
}

// Modal Component - AI Style
function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] bg-ai-bg-secondary border border-ai-border rounded-sm shadow-2xl animate-fade-in flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-ai-border flex-shrink-0">
          <h3 className="font-ai text-lg font-medium text-ai-text tracking-tight">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-ai-bg rounded-sm transition-colors"
          >
            <X className="w-5 h-5 text-ai-text-secondary" />
          </button>
        </div>
        {/* Content — scrollable */}
        <div className="p-5 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}


// Auth image for studio thumbnails — uses fetch+blob for internal API, regular img for external
function StudioAuthImage({ src, className, fallbackLabel }) {
  const [imgSrc, setImgSrc] = React.useState(null);
  const [hidden, setHidden] = React.useState(false);
  const isInternal = src && (src.includes('/api/recipes/storage/') || (API && src.startsWith(API)));

  React.useEffect(() => {
    if (!src) { setHidden(true); return; }
    if (!isInternal) {
      // External URL — use directly, no auth needed
      setImgSrc(src);
      return;
    }
    // Internal API URL — fetch with auth
    const token = localStorage.getItem('token');
    let blobUrl = null;
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { if (blob.size > 0) { blobUrl = URL.createObjectURL(blob); setImgSrc(blobUrl); } else setHidden(true); })
      .catch(() => setHidden(true));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [src]); // eslint-disable-line

  if (hidden || !imgSrc) return (
    <div className={className} style={{ background: 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(11,15,25,0.6) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'rgba(255,255,255,0.1)' }}>{(fallbackLabel || '?')[0]}</span>
    </div>
  );
  return <img src={imgSrc} alt="" className={className} onError={() => setHidden(true)} />;
}
